import React, { useCallback } from "react";
import { Text, View } from "@react-pdf/renderer";
import type { Style } from "@react-pdf/types";
import { styles, spacing } from "components/Resume/ResumePDF/styles";
import { DEBUG_RESUME_PDF_FLAG } from "lib/constants";
import { DEFAULT_FONT_COLOR } from "lib/redux/settingsSlice";
import { handleClick } from "lib/utils/navigator";

export const TemplateFourResumeSection = ({
  themeColor,
  heading,
  style = {},
  children,
}: {
  themeColor?: string;
  heading?: string;
  style?: Style;
  children: React.ReactNode;
}) => {
  const handleClickCallback = useCallback(
    () => handleClick(heading),
    [heading]
  );
  return (
    <div
      onClick={handleClickCallback}
      style={{
        ...styles.flexRow,
        gap: spacing["4"],
        marginTop: spacing["5"],
        cursor: "pointer",
      }}
    >
      {heading && (
        <View
          style={{
            ...styles.flexCol,
            gap: spacing["1"],
            width: "20vw",
            textAlign: "right",
          }}
        >
          <Text
            style={{
              fontWeight: "bold",
              letterSpacing: "0.3pt", // tracking-wide -> 0.025em * 12 pt = 0.3pt
              color: themeColor || DEFAULT_FONT_COLOR,
            }}
            debug={DEBUG_RESUME_PDF_FLAG}
          >
            {heading}
          </Text>
        </View>
      )}
      <View
        style={{
          ...styles.flexCol,
          gap: spacing[1.5],
          width: "80vw",
          borderLeft: "1px solid gray",
          paddingLeft: spacing["4"],
        }}
      >
        {children}
      </View>
    </div>
  );
};

// Enhanced version with orphan heading prevention for sections with subsections
export const TemplateFourResumeSectionWithSubsections = ({
  themeColor,
  heading,
  style = {},
  children,
}: {
  themeColor?: string;
  heading?: string;
  style?: Style;
  children: React.ReactNode;
}) => {
  const handleClickCallback = useCallback(
    () => handleClick(heading),
    [heading]
  );
  
  const groupedChildren = React.Children.toArray(children);
  const firstChild = groupedChildren[0];
  const restChildren = groupedChildren.slice(1);
  
  return (
    <div onClick={handleClickCallback} style={{ cursor: "pointer" }}>
      {/* Wrap heading with first child only to prevent orphan headings */}
      {heading && firstChild && (
        <View
          style={{
            ...styles.flexRow,
            gap: spacing["4"],
            marginTop: spacing["5"],
          }}
          wrap={false} // Only the container has wrap={false}, not the individual columns
        >
          <View
            style={{
              ...styles.flexCol,
              gap: spacing["1"],
              width: "20vw",
              textAlign: "right",
            }}
          >
            <Text
              style={{
                fontWeight: "bold",
                letterSpacing: "0.3pt",
                color: themeColor || DEFAULT_FONT_COLOR,
              }}
              debug={DEBUG_RESUME_PDF_FLAG}
            >
              {heading}
            </Text>
          </View>
          <View
            style={{
              ...styles.flexCol,
              gap: spacing[1.5],
              width: "80vw",
              borderLeft: "1px solid gray",
              paddingLeft: spacing["4"],
            }}
          >
            {firstChild}
          </View>
        </View>
      )}
      
      {/* Heading only (when no children) */}
      {heading && !firstChild && (
        <View
          style={{
            ...styles.flexRow,
            gap: spacing["4"],
            marginTop: spacing["5"],
          }}
        >
          <View
            style={{
              ...styles.flexCol,
              gap: spacing["1"],
              width: "20vw",
              textAlign: "right",
            }}
          >
            <Text
              style={{
                fontWeight: "bold",
                letterSpacing: "0.3pt",
                color: themeColor || DEFAULT_FONT_COLOR,
              }}
              debug={DEBUG_RESUME_PDF_FLAG}
            >
              {heading}
            </Text>
          </View>
          <View
            style={{
              ...styles.flexCol,
              gap: spacing[1.5],
              width: "80vw",
              borderLeft: "1px solid gray",
              paddingLeft: spacing["4"],
            }}
          />
        </View>
      )}
      
      {/* Rest of children - can break across pages */}
      {restChildren.length > 0 && restChildren.map((child, index) => (
        <View
          key={`rest-${index}`}
          style={{
            ...styles.flexRow,
            gap: spacing["4"],
          }}
        >
          <View style={{ width: "20vw" }} /> {/* Empty space to align with heading column */}
          <View
            style={{
              ...styles.flexCol,
              gap: spacing[1.5],
              width: "80vw",
              borderLeft: "1px solid gray",
              paddingLeft: spacing["4"],
            }}
          >
            {child}
          </View>
        </View>
      ))}
    </div>
  );
};

export const TemplateFourProfileSection = ({
  themeColor,
  heading,
  style = {},
  children,
}: {
  themeColor?: string;
  heading?: string;
  style?: Style;
  children: React.ReactNode;
}) => {
  return (
    <div
      style={{
        ...styles.flexRowBetween,
        alignItems: "center",
        marginTop: spacing["5"],
        backgroundColor: themeColor,
        padding: `${spacing["4"]} ${spacing["7"]}`,
      }}
    >
      {heading && (
        <View style={{ ...styles.flexCol, gap: spacing["1"] }}>
          <Text
            style={{
              fontWeight: "bold",
              letterSpacing: "0.3pt", // tracking-wide -> 0.025em * 12 pt = 0.3pt
              color: themeColor || DEFAULT_FONT_COLOR,
            }}
            debug={DEBUG_RESUME_PDF_FLAG}
          >
            {heading}
          </Text>
          {themeColor && (
            <View
              style={{
                height: "1.25pt",
                width: "100%",
                backgroundColor: "black",
                marginRight: spacing["3.5"],
              }}
              debug={DEBUG_RESUME_PDF_FLAG}
            />
          )}
        </View>
      )}
      {children}
    </div>
  );
};
